import type { TFunction } from 'i18next';
import { useState } from 'react';

import { MFA_TOTP_CONTRACT } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSInputLabel } from '@repo/sds/SDSInputLabel';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';

import emailSetupIntro from './assets/email-setup-intro.svg';
import { MfaHero } from './mfa-hero';
import { MfaInvalidCodeDialog } from './mfa-invalid-code-dialog';
import { MfaOtpInput } from './mfa-otp-input';
import { MfaSettingsPage } from './mfa-settings-page';
import { formatResendClock } from './mfa-sms-setup-utils';

const MFA = DataTestIds.COMMON.SECURITY.MFA;
const TOTP_CODE_DIGITS = MFA_TOTP_CONTRACT.digits;

export function MfaEmailSetupDialogs({
  actionError,
  busy,
  code,
  cooldownSeconds,
  email,
  isMobile,
  onBackToMethods,
  onCancel,
  onClearActionError,
  onConfirmSetup,
  onResendEmail,
  onSetupCodeChange,
  t,
}: {
  actionError?: string;
  busy: boolean;
  code: string;
  cooldownSeconds: number;
  email: string;
  isMobile: boolean;
  onBackToMethods: () => void;
  onCancel: () => void;
  onClearActionError: () => void;
  onConfirmSetup: () => void;
  onResendEmail: () => Promise<boolean>;
  onSetupCodeChange: (value: string) => void;
  t: TFunction<'common'>;
}) {
  const [phase, setPhase] = useState<'verify' | 'invalid'>('verify');
  const invalidMessage = t('security.mfa.invalidCode');

  // Derived rather than synced: an invalid-code error routes this dialog to
  // the invalid screen for as long as it's live, with nothing to reset once
  // it clears — `setPhase('verify')` below already moves it back manually.
  const effectivePhase = actionError === invalidMessage ? 'invalid' : phase;

  const stepCopy = (
    <SDSFlexbox direction="column" fullWidth gap="text-small">
      <SDSText color="primary" text={t('security.mfa.smsStep', { current: 2, total: 2 })} type="body-small" />
      <SDSText text={t('security.mfa.emailVerifyLead', { email })} type="body-medium" />
    </SDSFlexbox>
  );

  const resendMeta =
    cooldownSeconds > 0 ? (
      <SDSText
        color="muted"
        text={t('security.mfa.smsResendIn', { time: formatResendClock(cooldownSeconds) })}
        type="body-small"
      />
    ) : busy ? null : (
      <SDSPrimitiveButton dataTestId={MFA.EMAIL_RESEND} onClick={() => void onResendEmail()}>
        <SDSText color="primary" text={t('security.mfa.resend')} type="body-small" />
      </SDSPrimitiveButton>
    );

  const verifyFields = (
    <SDSFlexbox direction="column" fullWidth gap="text-medium">
      <SDSFlexbox direction="column" fullWidth gap="text-small">
        <SDSInputLabel isRequired label={t('security.mfa.smsCodeLabel')} />
        <MfaOtpInput onChange={onSetupCodeChange} value={code} />
      </SDSFlexbox>
      <div className="MfaSmsDialog__footerMeta">{resendMeta}</div>
    </SDSFlexbox>
  );

  if (effectivePhase === 'invalid') {
    return (
      <MfaInvalidCodeDialog
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.EMAIL_INVALID}
        description={t('security.mfa.emailInvalidDescription')}
        isMobile={isMobile}
        onClose={onCancel}
        primaryAction={{
          dataTestId: MFA.EMAIL_TRY_AGAIN,
          label: t('security.mfa.totpTryAgain'),
          onClick: () => {
            onSetupCodeChange('');
            onClearActionError();
            setPhase('verify');
          },
        }}
        secondaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel }}
        title={t('security.mfa.smsInvalidTitle')}
      />
    );
  }

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          <>
            <SDSButton
              dataTestId={MFA.TOTP_BACK}
              isDisabled={busy}
              mode="outline"
              onClick={onBackToMethods}
              text={t('security.mfa.totpBack')}
            />
            <SDSButton
              dataTestId={MFA.CONFIRM}
              isDisabled={busy ? false : code.length !== TOTP_CODE_DIGITS}
              isSubmitting={busy}
              onClick={onConfirmSetup}
              text={t('security.mfa.totpVerify')}
            />
          </>
        }
        ariaLabel={t('security.mfa.emailVerifyTitle')}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.EMAIL_SETUP}
        onBack={onCancel}
      >
        <SDSFlexbox alignItems="center" direction="column" fullWidth gap="text-medium">
          <SDSText text={t('security.mfa.emailVerifyTitle')} type="h4" />
          <MfaHero plain src={emailSetupIntro} />
        </SDSFlexbox>
        {stepCopy}
        {verifyFields}
        {actionError && actionError !== invalidMessage ? (
          <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
        ) : null}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className="MfaTotpDialog"
      dataTestId={MFA.EMAIL_SETUP}
      isOpen
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.CONFIRM,
        disabled: busy || code.length !== TOTP_CODE_DIGITS,
        label: t('security.mfa.totpVerify'),
        loading: busy,
        onClick: onConfirmSetup,
      }}
      secondaryAction={{
        dataTestId: MFA.TOTP_BACK,
        label: t('security.mfa.totpBack'),
        onClick: onBackToMethods,
      }}
      showCloseButton={false}
      size="small"
    >
      <SDSFlexbox direction="column" gap="text-x-large">
        <MfaHero src={emailSetupIntro} />
        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText text={t('security.mfa.emailVerifyTitle')} type="h4" />
          {stepCopy}
          {verifyFields}
          {actionError && actionError !== invalidMessage ? (
            <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
          ) : null}
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}
